<template>
  <GenericCard
    :locked="locked"
    has-delete
    :on-delete="onCardDelete"
    :on-action="submitForm"
    :on-refresh="onCardRefresh"
    :invalid="$v.section.$invalid"
    :dirty="$v.section.$anyDirty"
    :create="create"
    title="Residence History"
    :section="section"
    has-footer
    has-share
    :card-identifier="cardIdentifier"
    :survey-validation="surveyValidation"
    :survey-identifier="surveyIdentifier"
  >
    <v-form
      :disabled="locked"
      @submit.prevent="submitForm"
    >
      <v-row>
        <v-col>
          <v-text-field
            v-model="section.address"
            autocomplete="off"
            label="Address"
            outlined
            :error-messages="getErrorMessages($v.section.address)"
            @input="$v.section.address.$touch()"
            @blur="$v.section.address.$touch()"
          />
        </v-col>
        <v-col cols="3">
          <v-text-field
            v-model="section.county"
            autocomplete="off"
            label="County"
            outlined
            :error-messages="getErrorMessages($v.section.county)"
            @input="$v.section.county.$touch()"
            @blur="$v.section.county.$touch()"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-autocomplete
            v-model="section.country_id"
            :items="optionsCountries"
            item-text="name"
            item-value="id"
            label="Country"
            outlined
            :error-messages="getErrorMessages($v.section.country_id)"
            @input="$v.section.country_id.$touch()"
            @blur="$v.section.country_id.$touch()"
          />
        </v-col>
        <!-- State -->
        <v-col v-if="nonUSData">
          <v-text-field
            v-model="section.int_state"
            autocomplete="off"
            label="State/Province"
            outlined
            :error-messages="getErrorMessages($v.section.int_state)"
            @input="$v.section.int_state.$touch()"
            @blur="$v.section.int_state.$touch()"
          />
        </v-col>

        <v-col v-else>
          <v-autocomplete
            v-model="section.state"
            :items="optionsStates"
            label="State/Province"
            outlined
            :error-messages="getErrorMessages($v.section.state)"
            @input="$v.section.state.$touch()"
            @blur="$v.section.state.$touch()"
          />
        </v-col>
        <!-- City -->
        <v-col>
          <v-text-field
            v-model="section.city"
            autocomplete="off"
            label="City"
            outlined
            :error-messages="getErrorMessages($v.section.city)"
            @input="$v.section.city.$touch()"
            @blur="$v.section.city.$touch()"
          />
        </v-col>
        <!-- ZIP -->
        <v-col v-if="nonUSData">
          <v-text-field
            v-model="section.int_zip"
            autocomplete="off"
            label="Zip/Postal"
            outlined
            :error-messages="getErrorMessages($v.section.int_zip)"
            @input="$v.section.int_zip.$touch()"
            @blur="$v.section.int_zip.$touch()"
          />
        </v-col>

        <v-col v-else>
          <v-text-field
            v-model="section.zip"
            autocomplete="off"
            label="Zip/Postal"
            outlined
            :error-messages="getErrorMessages($v.section.zip)"
            @input="$v.section.zip.$touch()"
            @blur="$v.section.zip.$touch()"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="8"
        >
          <FormatSelectableComboDate
            class="mt-3 ml-1"
            label="Start Date"
            :formatable-date="section.start_date"
            :format="section.start_date_format"
            :uncertain="section.start_date_uncertain"
            required
            outlined
            :disabled="locked"
            for-party-edit
            :error-messages="getErrorMessages($v.section.start_date)"
            @update-box="onStartDateUncertainUpdate"
            @update-date="onStartDateUpdate"
            @tab-out="$v.section.start_date.$touch()"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="8"
        >
          <FormatSelectableComboDate
            class="mt-3 ml-1"
            label="End Date"
            :formatable-date="section.end_date"
            :format="section.end_date_format"
            :uncertain="section.end_date_uncertain"
            required
            outlined
            :disabled="locked"
            for-party-edit
            :error-messages="getErrorMessages($v.section.end_date)"
            @update-box="onEndDateUncertainUpdate"
            @update-date="onEndDateUpdate"
            @tab-out="$v.section.end_date.$touch()"
          />
        </v-col>
      </v-row>
    </v-form>
  </GenericCard>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

import { helpers } from '@vuelidate/validators'
import { rules } from '@/utilities/validation-rules'
import { useVuelidate } from '@vuelidate/core'

import { PARTY_NAMESPACE } from '@/store/party'
import { SHARED_NAMESPACE } from '@/store/shared'

import { alertHandler } from '@/alertHandler'

import FormatSelectableComboDate from '@/components/Fields/FormatSelectableComboDate'
import GenericCard from '@/components/Party/PartyEdit/SectionCards/GenericCard'

export default {
  name: 'ResidenceHistoriesCard',
  components: { FormatSelectableComboDate, GenericCard },

  props: {
    item: {
      type: Object,
      required: true,
    },

    surveyIdentifier: {
      type: String,
      default: null,
    },

    surveyValidation: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      $v: useVuelidate(),
      ...alertHandler(),
    }
  },

  data () {
    return {
      section: {},
    }
  },

  validations () {
    return {
      section: {
        address: {},
        country_id: {},
        county: {},
        end_date: {
          after_start_date: helpers.withMessage('End date must be after start date',
            _ => rules.startBeforeEndDate(this.section.start_date, this.section.end_date) === true),
        },

        end_date_uncertain: {},
        int_state: {},
        int_zip: {},
        non_us_data: {},
        start_date: {
          before_end_date: helpers.withMessage('Start date must be before end date',
            _ => rules.startBeforeEndDate(this.section.start_date, this.section.end_date) === true),
        },

        start_date_uncertain: {},
        state: {},
        zip: {},
      },
    }
  },

  computed: {
    cardIdentifier () { return `residence-history-card-${this.sectionIdentifier}` },
    create () { return this.item.id === null },

    locked: {
      get () {
        return this.peResidenceHistoriesCardLock(this.sectionIdentifier)
      },

      set (value) {
        this.setPeResidenceHistoriesCardLock({ index: this.sectionIdentifier, locked: value })
      },
    },

    nonUSData () {
      return this.optionsCountries.find(entry => entry.id === this.section.country_id)?.name !== 'USA'
    },

    optionsCountries () { return this.getCachedDropdown('countries') },

    optionsStates () {
      return this.getCachedDropdown('us_state/states')
    },

    sectionIdentifier () { return this.section.id || 'new' },

    ...mapGetters(PARTY_NAMESPACE, [
      'peParty',
      'peResidenceHistoriesCardLock',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'getCachedDropdown',
    ]),
  },

  watch: {
    item: {
      immediate: true,

      handler: function () {
        this.section = { ...this.item }
      },

      deep: true,
    },

    'nonUSData' (val) {
      if (val) {
        this.section.state = null
        this.section.zip = null
      } else {
        this.section.int_state = null
        this.section.int_zip = null
      }
    },
  },

  created () {
    if (!this.create) this.initPeResidenceHistoriesCard(this.sectionIdentifier)

    this.getDropdownOptions({ options: false, path: 'countries' })
    this.getDropdownOptions({ path: 'us_state/states' })
  },

  methods: {
    getErrorMessages (validator) { return validator?.$errors.map(e => e.$message) },

    async onCardCreate () {
      if (this.section.country_id !== null) this.section.non_us_data = this.nonUSData
      return this.peResidenceHistoriesCardCreate({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardDelete () {
      if (this.create) return this.peResidenceHistoriesCardCreateUndo()
      else {
        return this.peResidenceHistoriesCardDelete({ id: this.section.id, index: this.sectionIdentifier })
      }
    },

    async onCardRefresh () {
      return this.peResidenceHistoriesCardRefresh({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardUpdate () {
      if (this.section.country_id !== null) this.section.non_us_data = this.nonUSData
      return this.peResidenceHistoriesCardUpdate({
        id: this.section.id,
        index: this.sectionIdentifier,
        section: this.section,
      })
    },

    onEndDateUncertainUpdate (value) {
      this.$v.section.end_date.$touch()
      this.$v.section.end_date_uncertain.$touch()
      this.section.end_date_uncertain = value
    },

    onEndDateUpdate (value) {
      this.$v.section.end_date.$touch()
      this.$v.section.end_date_uncertain.$touch()
      this.section.end_date = value.date
      this.section.end_date_format = value.format
    },

    async onSectionUnlock () {
      return this.setPeResidenceHistoriesCardLock({ index: this.sectionIdentifier, locked: false })
    },

    onStartDateUncertainUpdate (value) {
      this.$v.section.start_date.$touch()
      this.$v.section.start_date_uncertain.$touch()
      this.section.start_date_uncertain = value
    },

    onStartDateUpdate (value) {
      this.$v.section.start_date.$touch()
      this.$v.section.start_date_uncertain.$touch()
      this.section.start_date = value.date
      this.section.start_date_format = value.format
    },

    async submitForm () {
      if (this.locked) return this.onSectionUnlock()
      else if (this.create) return this.onCardCreate()
      else return this.onCardUpdate()
    },

    ...mapMutations(PARTY_NAMESPACE, [
      'peResidenceHistoriesCardCreateUndo',
      'setPeResidenceHistoriesCardLock',

    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'initPeResidenceHistoriesCard',
      'peResidenceHistoriesCardCreate',
      'peResidenceHistoriesCardDelete',
      'peResidenceHistoriesCardRefresh',
      'peResidenceHistoriesCardUpdate',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'getDropdownOptions',
    ]),
  },
}
</script>
